import { Brain } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAiCalls, useAiQueueDigest, useAiUsage } from "~/api/queries";
import type { AiCall, AiUsage } from "~/api/types";
import { Metric, Pill, Row, Rows } from "~/features/automation/components";
import { formatCompact, formatDuration } from "~/features/automation/format";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { formatRelativeTime } from "~/foundation/utils/time";

/** micros → dollars. Small sums keep four decimals so $0.0042 does not round to nothing. */
function formatUsd(costMicros: number, locale: string): string {
  const usd = costMicros / 1_000_000;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: usd !== 0 && Math.abs(usd) < 1 ? 4 : 2,
  }).format(usd);
}

/**
 * The server's own caveats about how its figures were made ("engine pricing is a placeholder
 * rate", …). Rendered muted but always rendered — a cost whose caveat was dropped is a
 * different, wrong claim.
 */
function Reading({ sentences }: { sentences: string[] }) {
  if (sentences.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col gap-0.5">
      {sentences.map((sentence) => (
        <p key={sentence} className="text-small text-text-tertiary">
          {sentence}
        </p>
      ))}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-body font-medium text-text">{title}</h3>
      {children}
    </div>
  );
}

function MetricsBand({ usage }: { usage: AiUsage }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      <Metric
        label={t("Model calls")}
        value={formatCompact(usage.totals.calls, i18n.language)}
        hint={t("the last {{count}} days", { count: usage.windowDays })}
      />
      <Metric label={t("Tokens")} value={formatCompact(usage.totals.llmTokens, i18n.language)} />
      <Metric label={t("Cost")} value={formatUsd(usage.totals.costMicros, i18n.language)} />
      <Metric
        label={t("Engine runs")}
        value={formatCompact(usage.engines.runs, i18n.language)}
        hint={t("statistical — no LLM")}
      />
    </div>
  );
}

/**
 * Every model in one table, tagged with the lane it was seen in — the lanes are different
 * ledgers at different grains, so rows are listed side by side, never merged. Pulse rows keep
 * measured and estimated tokens apart (summing them would republish chars/4 guesses as data),
 * and a price from the fallback rate says so.
 */
function ByModel({ usage }: { usage: AiUsage }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const empty =
    usage.pulse.byModel.length === 0 &&
    usage.actions.byModel.length === 0 &&
    usage.engines.engines.length === 0;

  if (empty) {
    return <Text variant="small">{t("No model has been called in this window.")}</Text>;
  }

  const pulseTokens = (measured: number, estimated: number): string => {
    const parts: string[] = [];
    if (measured > 0) {
      parts.push(t("{{tokens}} measured", { tokens: formatCompact(measured, locale) }));
    }
    if (estimated > 0) {
      parts.push(t("~{{tokens}} estimated", { tokens: formatCompact(estimated, locale) }));
    }
    return parts.length > 0 ? parts.join(" · ") : "—";
  };

  return (
    <Rows>
      {usage.pulse.byModel.map((row) => (
        <Row key={`pulse-${row.model}`}>
          <div className="min-w-0 flex-1">
            <p className="truncate font-mono text-body text-text">{row.model}</p>
            <p className="truncate text-small text-text-tertiary">
              {row.thinkerIds.join(" · ")} · {t("{{count}} calls", { count: row.calls })}
              {row.failures > 0 ? ` · ${t("{{count}} failed", { count: row.failures })}` : ""}
            </p>
          </div>
          <Pill tone="accent">{t("pulse")}</Pill>
          {row.rateKnown ? null : <Pill tone="warning">{t("fallback rate")}</Pill>}
          <span className="hidden text-small text-text-secondary tabular-nums md:inline">
            {pulseTokens(row.measuredTokens, row.estimatedTokens)}
          </span>
          <span className="hidden w-14 text-end text-small text-text-tertiary tabular-nums sm:inline">
            {formatDuration(row.durationMs, t)}
          </span>
          <span className="w-20 text-end text-small text-text tabular-nums">
            {formatUsd(row.costMicros, locale)}
          </span>
        </Row>
      ))}
      {usage.actions.byModel.map((row) => (
        <Row key={`actions-${row.model}`}>
          <div className="min-w-0 flex-1">
            <p className="truncate font-mono text-body text-text">{row.model}</p>
            <p className="truncate text-small text-text-tertiary">
              {row.thinkerIds.join(" · ")} · {t("{{count}} calls", { count: row.calls })}
            </p>
          </div>
          <Pill>{t("actions")}</Pill>
          {row.rateKnown ? null : <Pill tone="warning">{t("fallback rate")}</Pill>}
          <span className="hidden text-small text-text-secondary tabular-nums md:inline">
            {t("{{in}} in · {{out}} out", {
              in: formatCompact(
                row.inputTokens + row.cacheWriteTokens + row.cacheReadTokens,
                locale,
              ),
              out: formatCompact(row.outputTokens, locale),
            })}
          </span>
          <span className="hidden w-14 sm:inline" aria-hidden />
          <span className="w-20 text-end text-small text-text tabular-nums">
            {formatUsd(row.costMicros, locale)}
          </span>
        </Row>
      ))}
      {usage.engines.engines.map((row) => (
        <Row key={`engine-${row.engine}`}>
          <div className="min-w-0 flex-1">
            <p className="truncate font-mono text-body text-text">{row.engine}</p>
            <p className="truncate text-small text-text-tertiary">
              {t("{{count}} runs", { count: row.runs })}
              {row.failed > 0 ? ` · ${t("{{count}} failed", { count: row.failed })}` : ""}
            </p>
          </div>
          <Pill>{t("statistical — no LLM")}</Pill>
          <span className="hidden w-14 text-end text-small text-text-tertiary tabular-nums sm:inline">
            {formatDuration(row.durationMs, t)}
          </span>
          <span className="w-20 text-end text-small text-text tabular-nums">
            {formatUsd(row.costMicros, locale)}
          </span>
        </Row>
      ))}
    </Rows>
  );
}

function ByFlow({ usage }: { usage: AiUsage }) {
  const { t, i18n } = useTranslation();

  if (usage.byFlow.length === 0) {
    return <Text variant="small">{t("No flow has spent anything in this window.")}</Text>;
  }

  return (
    <Rows>
      {usage.byFlow.map((row) => (
        <Row key={row.flow}>
          <p className="min-w-0 flex-1 truncate text-body text-text">{row.flow}</p>
          <span className="hidden w-20 text-end text-small text-text-tertiary tabular-nums sm:inline">
            {t("{{count}} calls", { count: row.calls })}
          </span>
          <span className="hidden w-20 text-end text-small text-text-secondary tabular-nums md:inline">
            {formatCompact(row.tokens, i18n.language)}
          </span>
          <span className="w-20 text-end text-small text-text tabular-nums">
            {formatUsd(row.costMicros, i18n.language)}
          </span>
        </Row>
      ))}
    </Rows>
  );
}

/** A refusal or a timeout is a row like any other — never hidden behind an average. */
function OutcomePill({ outcome }: { outcome: string }) {
  const ok = outcome === "ok";
  const label = outcome.length > 26 ? `${outcome.slice(0, 25)}…` : outcome;
  return <Pill tone={ok ? "success" : "danger"}>{label}</Pill>;
}

function CallRow({ call }: { call: AiCall }) {
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const tokens =
    call.tokensTotal === null
      ? "—"
      : `${call.tokensMeasured ? "" : "~"}${formatCompact(call.tokensTotal, locale)}`;

  return (
    <Row>
      <span className="hidden w-20 shrink-0 text-small text-text-tertiary sm:inline">
        {formatRelativeTime(call.at, locale)}
      </span>
      <Pill tone={call.lane === "pulse" ? "accent" : "neutral"}>{call.lane}</Pill>
      <div className="min-w-0 flex-1">
        <p className="truncate text-body text-text">{call.what}</p>
        <p className="truncate text-small text-text-tertiary">
          {call.thinkerId ?? "—"} → {call.model ?? "—"}
          {call.flow ? ` · ${call.flow}` : ""}
          {call.sessionId ? (
            <>
              {" · "}
              <span className="font-mono" title={call.sessionId}>
                {call.sessionId.length > 14 ? `${call.sessionId.slice(0, 13)}…` : call.sessionId}
              </span>
            </>
          ) : null}
        </p>
      </div>
      <span className="hidden w-14 text-end text-small text-text-secondary tabular-nums md:inline">
        {tokens}
      </span>
      <span className="hidden w-16 text-end text-small text-text-tertiary tabular-nums md:inline">
        {call.costMicros > 0 ? formatUsd(call.costMicros, locale) : "—"}
      </span>
      <OutcomePill outcome={call.outcome} />
    </Row>
  );
}

function RecentCalls() {
  const { t } = useTranslation();
  const { data, isLoading } = useAiCalls({ limit: 50 });

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }
  if (!data?.calls.length) {
    return <Text variant="small">{t("Nothing has called a model in this window.")}</Text>;
  }

  return (
    <div className="flex flex-col gap-2">
      <Rows>
        {data.calls.map((call) => (
          <CallRow key={`${call.at}-${call.lane}-${call.target ?? call.what}`} call={call} />
        ))}
      </Rows>
      <Reading sentences={data.reading} />
    </div>
  );
}

/** The article-digestion pipeline: DB backlog, broker depth, dead letters, measured drain. */
function DigestionCard() {
  const { t, i18n } = useTranslation();
  const { data: digest, isLoading } = useAiQueueDigest();

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }
  if (!digest) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 rounded-md border border-divider p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={digest.transport === "queue" ? "accent" : "neutral"}>
          {digest.transport === "queue" ? t("queue") : t("inline")}
        </Pill>
        {digest.payloadEncryption ? <Pill>{t("encrypted payloads")}</Pill> : null}
        <Text variant="small">
          {digest.transport === "queue"
            ? t("Articles ride the message broker, one per message.")
            : t("Articles are digested in-process — no broker in the path.")}
        </Text>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Metric
          label={t("Waiting")}
          value={formatCompact(digest.backlog.pendingArticles, i18n.language)}
          hint={t("articles awaiting extraction")}
        />
        <Metric
          label={t("In the queue")}
          value={digest.queue ? formatCompact(digest.queue.messages, i18n.language) : "—"}
          hint={
            digest.queue
              ? t("{{count}} consumers · scales {{min}}–{{max}} at {{depth}}", {
                  count: digest.queue.consumers,
                  min: digest.queue.band.min,
                  max: digest.queue.band.max,
                  depth: digest.queue.band.scaleUpAt,
                })
              : t("no broker in the path")
          }
        />
        <Metric
          label={t("Dead letters")}
          value={formatCompact(digest.deadLetters, i18n.language)}
          hint={t("failed twice, kept for inspection")}
        />
        <Metric
          label={t("Drained")}
          value={t("{{count}}/hour", { count: digest.drain.lastHour })}
          hint={t("{{count}} in the last day", { count: digest.drain.lastDay })}
        />
      </div>

      {digest.deadLetters > 0 ? (
        <p className="rounded-sm bg-danger/10 px-3 py-2 text-small text-danger">
          {t("{{count}} messages failed twice and are held for inspection.", {
            count: digest.deadLetters,
          })}
        </p>
      ) : null}
      <p className="text-small text-text-tertiary">{digest.backlog.note}</p>
    </div>
  );
}

/**
 * The workspace's AI spend, read from the platform's central ledger — four ledgers at four
 * grains, reported side by side. Everything here is read-only; estimates arrive flagged and
 * stay flagged.
 */
export function Ai() {
  const { t } = useTranslation();
  const { data: usage, isLoading } = useAiUsage();

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }
  if (!usage) {
    return <Empty icon={Brain} message={t("The AI ledger is not available here.")} />;
  }

  return (
    <div className="flex flex-col gap-5">
      <Text variant="small">
        {t(
          "Every model call this workspace made — which model, which session, which flow, and what it cost.",
        )}
      </Text>

      <MetricsBand usage={usage} />
      <Reading sentences={usage.reading} />

      <Section title={t("By model")}>
        <ByModel usage={usage} />
      </Section>

      <Section title={t("By flow")}>
        <ByFlow usage={usage} />
      </Section>

      <Section title={t("Recent calls")}>
        <RecentCalls />
      </Section>

      <Section title={t("Article digestion")}>
        <DigestionCard />
      </Section>
    </div>
  );
}
